import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Grid, Text, Spinner, Button } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import useAuth from 'hooks/useAuth';
import {
  getInboxOptions, getRequests, READ_ROLES, EDIT_ROLES, STATUS, PAYMENT, CHANNEL_LABEL, documentLabel, formatDate,
} from 'api/administrador/documentRequests';
import { StatusPill } from 'components/monitoring/ai/common';
import RequestDrawer from './RequestDrawer';
import NewRequestModal from './NewRequestModal';

const COLS = '110px 1.6fr 1fr 1.1fr 0.9fr 1fr';

/** Pestaña «Solicitudes» en Ver estudiante: sus solicitudes de documentos. */
export default function StudentRequestsTab({ student }) {
  const { user } = useAuth();
  const role = user?.role?.type;
  const isAllowed = READ_ROLES.includes(role);
  const canEdit = EDIT_ROLES.includes(role);
  const studentId = student?.id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const list = await getRequests({ studentId, pageSize: 50 });
      setRows(list?.data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[solicitudes] error cargando las del estudiante:', e);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (!isAllowed) return;
    load();
    getInboxOptions().then(setOptions).catch(() => setOptions(null));
  }, [isAllowed, load]);

  // Memorizado: el modal se reinicia cuando cambia este objeto.
  const initialStudent = useMemo(() => (student
    ? {
      id: student.id,
      name: [student.user?.first_name, student.user?.last_name].filter(Boolean).join(' '),
      identity: student.identity,
      email: student.user?.email,
      cellphone: student.user?.cellphone,
    }
    : null), [student]);

  if (!isAllowed) return <Text color="gray.500" fontSize="sm">Disponible solo para el área administrativa.</Text>;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} gap={3} wrap="wrap">
        <Text fontSize="sm" color="gray.600">Solicitudes de documentos de este estudiante.</Text>
        {canEdit && (
          <Button size="sm" leftIcon={<FiPlus />} variant="outline" borderRadius="8px" onClick={() => setCreating(true)}>
            Nueva solicitud
          </Button>
        )}
      </Flex>

      <Box border="1px solid" borderColor="gray.200" borderRadius="12px" overflowX="auto">
        <Box minW="680px">
          <Grid templateColumns={COLS} px={4} py={2.5} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase" gap={3}>
            <Text>Número</Text><Text>Documento</Text><Text>Canal</Text><Text>Estado</Text><Text>Pago</Text><Text>Solicitada</Text>
          </Grid>
          {loading && <Flex justify="center" py={6}><Spinner color="#574feb" /></Flex>}
          {!loading && rows.length === 0 && (
            <Text textAlign="center" py={6} color="gray.500" fontSize="sm">No tiene solicitudes registradas.</Text>
          )}
          {rows.map((r) => (
            <Grid
              key={r.id} templateColumns={COLS} px={4} py={2.5} gap={3} alignItems="center" borderTop="1px solid" borderColor="gray.100"
              cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => setOpenId(r.id)}
            >
              <Text fontSize="sm" fontFamily="mono">{r.number}</Text>
              <Text fontSize="sm" isTruncated>{documentLabel(r)}</Text>
              <Text fontSize="sm" color="gray.700">{CHANNEL_LABEL[r.channel] || r.channel}</Text>
              <Box>{STATUS[r.status] ? <StatusPill label={STATUS[r.status].label} tone={STATUS[r.status].tone} /> : r.status}</Box>
              <Box>{PAYMENT[r.paymentStatus] ? <StatusPill label={PAYMENT[r.paymentStatus].label} tone={PAYMENT[r.paymentStatus].tone} /> : '—'}</Box>
              <Text fontSize="sm" color="gray.600">{formatDate(r.requestedAt)}</Text>
            </Grid>
          ))}
        </Box>
      </Box>

      <RequestDrawer
        requestId={openId}
        isOpen={!!openId}
        onClose={() => setOpenId(null)}
        options={options}
        canEdit={canEdit}
        onChanged={load}
      />
      <NewRequestModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        options={options}
        initialStudent={initialStudent}
        onCreated={(r) => { load(); if (r?.id) setOpenId(r.id); }}
      />
    </Box>
  );
}
